import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Settings,
  MapPin,
  Users,
  Shield,
  Briefcase,
  UsersIcon,
  Store,
  Mail,
  Send,
  ClipboardList,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  CreditCard,
  X,
} from "lucide-react";

// Import all settings components
import { GeneralSettingsTab } from "@/components/settings/GeneralSettingsTab";
import {
  LocationsSettingsTab,
  LocationFormPanel,
  RoomFormInLocation,
} from "@/components/settings/LocationsSettingsTab";
import {
  MembersSettingsTab,
  InviteMemberPanel,
  MemberFormPanel,
} from "@/components/settings/MembersSettingsTab";
import {
  TeamsSettingsTab,
  TeamFormPanel,
} from "@/components/settings/TeamsSettingsTab";
import {
  RolesSettingsTab,
  RoleFormPanel,
} from "@/components/settings/RolesSettingsTab";
import {
  ServicesSettingsTab,
  ServiceFormPanel,
  ServiceQRCodePanel,
} from "@/components/settings/ServicesSettingsTab";
import {
  TemplatesSettingsTab,
  TemplateFormPanel,
} from "@/components/settings/TemplatesSettingsTab";
import {
  MarketplaceSettingsTab,
  AppDetailsPanel,
} from "@/components/settings/MarketplaceSettingsTab";
import { ConsultationBundlesAppDetails } from "@/components/settings/ConsultationBundlesAppDetails";
import {
  PreConsultSettingsTab,
  PreConsultTemplateFormPanel,
  PreConsultTemplateBuilderPanel,
} from "@/components/settings/PreConsultSettingsTab";
import {
  ConsultationSettingsTab,
  ConsultationFormPanel,
} from "@/components/settings/ConsultationSettingsTab";

// Import initial data from data files
import {
  initialTemplates,
  useTemplateSettingsData,
} from "@/lib/settings/templatesData";
import { initialRoles } from "@/lib/settings/rolesData";
import { initialTeams } from "@/lib/settings/teamsData";
import { useLocationsSettingsData } from "@/lib/settings/locationsData";
import { usePreConsultData } from "@/lib/settings/preConsultData";
import { useSettingsTabSwitcher } from "@/lib/settingsData";
import setinviteTeam from "@/Model/Inviteteam/setinviteTeam";
import { inviteMemberMultipleTeams } from "@/dispatchers/teamDispatchers";
import { showError } from "@/components/ToastedMessage/ToastedMessage";
import {
  updateMemberV2,
  getPendingInvites,
  deleteInvite,
} from "@/dispatchers/memberDispatchers";
import useTitanTeam from "@/hooks/useTitanTeam";
import { deleteUserById } from "@/dispatchers/memberDispatchers";
import { getmembers } from "@/dispatchers/memberDispatchers";
import {
  deleteAiTemplateById,
  deletePreConsultTemplateById,
  setAiTemplate,
} from "@/dispatchers/templateDispatchers";
import {
  getAiTemplateByCompanyId,
  getPreConsultTemplateByCompanyId,
} from "@/dispatchers/templateDispatchers";
import { getPatientConsultationsCount } from "@/dispatchers/patientDispatchers";
import { deleteNewService } from "@/dispatchers/typeDispatchers";
import { getDefaultCompanyServices } from "@/dispatchers/pharmacyServiceDispatchers";
import { useTitanApp } from "@/hooks/useTitanApp";

import {
  getServicesByPharmacyId,
  addRoom as addPharmacyRoom,
  updateRoom as editPharmacyRoom,
} from "@/dispatchers/locationDispatchers";
import { UpdatePharmacyLocation } from "@/dispatchers/pharmacyServiceDispatchers";

import { date } from "zod";
import { ObjectId } from "bson";
import {
  updatePreConsultTemplates,
  savePreConsultTemplates,
  deleteTemplates,
  getEmailsAndSmsTemplates,
} from "@/dispatchers/templateDispatchers";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { deleteConfirmationMessage } from "@/data/constantStrings";
import { BillingSettingsTab } from "./settings/BillingSettingsTab";
import { getPharmaciesByCompanyId } from "@/dispatchers/pharmacyServiceDispatchers";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import {
  setShowSettingsModal,
  setSettingsActiveTab,
  setShowPermissionModal,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseTemplateUpsert,
  accessTitanverseTemplateView,
  accessTitanversUserUpsert,
  accessTitanversUserView,
  accessTitanverseTeamView,
  accessTitanverseTeamUpsert,
  accessTitanverseFullAccess,
  accessTitanverseManageSettings,
} from "@/data/constantStrings";
import PermissionRequiredModal from "./PermissionRequiredModal";
import {
  resendEmailToUser,
  reinviteEmailToUser,
} from "@/dispatchers/onboardingVerificationDsipatchers";
import { AlliedHealthTrainingPgdAppDetails } from "./settings/AlliedHealthTrainingPgdAppDetails";
import {
  alliedHealthPgds,
  getPgdIdForServiceId,
} from "@/lib/settings/alliedHealthPgdData";
import { getCompanyMarketplaceAppsByCompanyId } from "@/dispatchers/marketAppDispacher";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";

interface SettingsModalProps {
  isDarkMode?: boolean;
  trigger: React.ReactNode;
}

type SettingsTab =
  | "general"
  | "locations"
  | "members"
  | "roles"
  | "services"
  | "teams"
  | "marketplace"
  | "templates"
  | "preconsult"
  | "consultation"
  | "billing";

interface TabItem {
  id: SettingsTab;
  label: string;
  icon: any;
  children?: TabItem[];
}

const tabs: TabItem[] = [
  { id: "general", label: "General", icon: Settings },
  { id: "locations", label: "Sites", icon: MapPin },
  { id: "members", label: "Members", icon: Users },
  { id: "teams", label: "Teams", icon: UsersIcon },
  { id: "roles", label: "Roles", icon: Shield },
  { id: "services", label: "Services", icon: Briefcase },
  {
    id: "templates",
    label: "Templates",
    icon: Mail,
    children: [
      { id: "templates", label: "Documents", icon: Send },
      { id: "preconsult", label: "Pre-Consult Template", icon: ClipboardList },
      { id: "consultation", label: "Consultation", icon: MessageSquare },
    ],
  },

  { id: "marketplace", label: "Market Place", icon: Store },
  { id: "billing", label: "Billing", icon: CreditCard },
];

export default function SettingsModal({
  isDarkMode,
  trigger,
}: SettingsModalProps) {
  // const { initialLocations } = useLocationsSettingsData();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["templates"]),
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState<() => void>(() => () => {});
  const [deleteMessage, setDeleteMessage] = useState(
    "Are you sure you want to delete this item?",
  );

  // Form visibility states
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [showAppDetails, setShowAppDetails] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showPreConsultForm, setShowPreConsultForm] = useState(false);
  const [showPreConsultBuilder, setShowPreConsultBuilder] = useState(false);

  // Edit states
  const [editingService, setEditingService] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [defaultTemplateType, setDefaultTemplateType] = useState<
    "email" | "sms" | "letter" | undefined
  >();
  const [
    editingConsultationTemplate,
    setEditingConsultationTemplate,
  ] = useState(null);
  const [editingPreConsultTemplate, setEditingPreConsultTemplate] = useState(
    null,
  );

  // Selection states
  const [selectedService, setSelectedService] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const { emailAndSmsTemplates } = useTemplateSettingsData();

  // Data states
  const [templates, setTemplates] = useState(emailAndSmsTemplates);
  const {
    initialLocations,
    isPharmacyServiceLoading,
  } = useLocationsSettingsData();
  const [locations, setLocations] = useState(initialLocations);
  const { preConsultTemplatesData } = usePreConsultData();

  const [preConsultTemplates, setPreConsultTemplates] = useState(
    preConsultTemplatesData,
  );

  const [roles, setRoles] = useState(initialRoles);
  const [services, setServices] = useState<Service[]>([]);

  const [teams, setTeams] = useState(initialTeams);
  const [consultationTemplates, setConsultationTemplates] = useState([]);
  const [members, setMembers] = useState([]);

  const { templateState } = useTitanApp();
  const { isConsultationSummaryLoading } = templateState || {};
  const [pendingInvites, setPendingInvites] = useState([]);

  const [installedApps, setInstalledApps] = useState(
    new Map([
      [
        "titan",
        { locations: ["1", "2", "3"], apiKeys: { "1": {}, "2": {}, "3": {} } },
      ],
    ]),
  );
  const { switchTabWithData } = useSettingsTabSwitcher();
  const { pharmacyServiceState, generalsState } = useTitanApp();
  const {
    allPharmacies: availablePharmacies,
    isUpdatePharmacyLocationLoading,
    defaultPharmacyServices,
  } = pharmacyServiceState || {};
  const { appState } = useTitanApp();
  const {
    currentTeamId,
    companyId,
    currentUser,
    features,
    companyDetails,
    showSettingsModal,
    settingsActiveTab,
    showPermissionModal,
    showPermissionMessage,
    globalTabText,
  } = appState || {};

  const { companySubscriptionStatus } = companyDetails || {};
  const {
    ifKanbanSearchBoxEnabled,
    ifBillingEnabled,
    ifAIConsultationEnabled,
  } = features || {};
  const { teamState } = useTitanTeam();
  const { roles: availableRoles, isRolesLoading, teamRolesAll } = teamState;
  const { generalData } = generalsState || {};

  useEffect(() => {
    if (initialLocations) {
      setLocations(initialLocations);
    }
  }, [initialLocations]);

  // Restores Allied Health Training PGD install state (green badges) after a refresh,
  // since it isn't tracked anywhere else once installedApps resets on mount.
  useEffect(() => {
    const fetchAlliedHealthInstalls = async () => {
      if (!companyId) return;

      // availablePharmacies (shared Redux slice) isn't reliably refreshed
      // when the active company changes, so fetch pharmacies scoped to
      // this company directly instead of trusting that shared state.
      const companyPharmacies = await getPharmaciesByCompanyId(companyId);
      if (!companyPharmacies?.length) return;

      const companyMarketApps = await getCompanyMarketplaceAppsByCompanyId(
        companyId,
      );
      if (!companyMarketApps) return;

      const ahtEntries = companyMarketApps.filter(
        (entry: any) => entry.marketType === 5,
      );
      if (ahtEntries.length === 0) return;

      setInstalledApps((prev) => {
        const newMap = new Map(prev);

        ahtEntries.forEach((entry: any) => {
          const entryOds = entry.ods || "";
          const pharmacy = companyPharmacies.find((p: any) => {
            const pOds = p?.odsCode ?? p?.ODSCode ?? p?.ods ?? "";
            return pOds === entryOds;
          });
          const locationId = pharmacy?.pharmacyID;
          if (!locationId) return;

          let payload;
          try {
            payload = entry.payLoad ? JSON.parse(entry.payLoad) : null;
          } catch {
            payload = null;
          }
          const payloadPgds = payload?.pgds || [];

          // EnabledServices is the source of truth for what's installed;
          // PayLoad only supplies pricing/installedAt metadata when available.
          const pgds = (entry.enabledServices || [])
            .map((service: any) => {
              const pgdId = getPgdIdForServiceId(service.serviceId);
              if (!pgdId) return null;
              const existingRecord = payloadPgds.find(
                (r: any) => r.pgdId === pgdId,
              );
              if (existingRecord) return existingRecord;

              const catalogPgd = alliedHealthPgds.find((p) => p.id === pgdId);
              return {
                pgdId,
                installedAt: entry.createdOn || new Date().toISOString(),
                pricePerTransaction: catalogPgd?.pricePerTransaction || 0,
                monthlyCapPerSite: catalogPgd?.monthlyCapPerSite || 0,
              };
            })
            .filter(Boolean);

          const existing = newMap.get(entry.marketAppID) || {
            locations: [],
            apiKeys: {},
          };
          newMap.set(entry.marketAppID, {
            locations: [...new Set([...existing.locations, locationId])],
            apiKeys: { ...existing.apiKeys, [locationId]: { pgds } },
          });
        });

        return newMap;
      });
    };

    fetchAlliedHealthInstalls();
  }, [companyId]);

  // keep preConsultTemplates in sync
  useEffect(() => {
    if (preConsultTemplatesData) {
      setPreConsultTemplates(preConsultTemplatesData);
    }
  }, [preConsultTemplatesData]);

  useEffect(() => {
    setOpen(showSettingsModal);
  }, [showSettingsModal]);

  useEffect(() => {
    if (settingsActiveTab) {
      setActiveTab(settingsActiveTab);
    }
  }, [settingsActiveTab]);

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    setShowSettingsModal(newOpen);
    if (!newOpen) {
      setSettingsActiveTab("general");
    }
  };

  // Generic handlers
  const createHandler = (setData, setShowForm, setEditing) => async (data) => {
    if (setEditing) {
      setEditing(data);
    }
    setShowForm(true);
  };

  const daysMap = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  interface TimeBlock {
    from: string;
    to: string;
  }

  interface DayAvailability {
    enabled: boolean;
    timeBlocks: TimeBlock[];
  }

  type Availability = Record<string, DayAvailability>;
  const reverseAvailability = (availability: Availability) => {
    return Object.entries(availability || {})
      .map(([dayName, { enabled, timeBlocks }]) => {
        if (!enabled || timeBlocks.length === 0) return null;

        return {
          dayOfWeek: daysMap.indexOf(dayName), // back to number
          slots: timeBlocks.map((block) => ({
            startTime: block.from,
            endTime: block.to,
          })),
        };
      })
      .filter(Boolean);
  };

  const reverseRooms = (rooms, servicesData) => {
    return (rooms || []).map((room) => ({
      _id:
        room.id && ObjectId.isValid(room.id)
          ? room.id
          : new ObjectId().toHexString(),
      roomName: room.name,
      services: (room.services || []).map((s) => {
        var currentServices = servicesData ?? services;
        const fullService = currentServices.find((srv) => srv.name === s);
        return fullService ? fullService.serviceData : { serviceName: s };
      }),
      availability: reverseAvailability(room.availability),
      active: room.active,
    }));
  };

  const reverseOpeningHours = (openingHours) => {
    if (!openingHours) return [];
    return daysMap.flatMap((day, index) => {
      const info = openingHours[day];
      if (info && info.enabled && info.open && info.close) {
        return [
          {
            dayOfWeek: index,
            slots: [{ startTime: info.open, endTime: info.close }],
          },
        ];
      }
      return [];
    });
  };

  const savePharmacyLocation = async (data, servicesData) => {
    const payload = {
      pharmacyID: data.id,
      pharmacyName: data.name || "",
      pharmacyAddress: data.address || "",
      active: data.active ?? true,
      pharmacyRooms: reverseRooms(data.rooms || [], servicesData),
      emailAddress: data.email || "",
      phoneNumber: data.phone || "",
      openingHours: reverseOpeningHours(data.openingHours),
    };
    await UpdatePharmacyLocation(payload);
  };

  const saveHandler = (setData, setShowForm, setEditing, editingData) => async (
    data,
  ) => {
    if (editingData) {
      setData((prev) =>
        prev.map((item) =>
          item.id === editingData.id ? { ...data, id: editingData.id } : item,
        ),
      );
    } else {
      setData((prev) => [...prev, { ...data, id: Date.now().toString() }]);
    }

    setShowForm(false);
    if (setEditing) setEditing(null);
  };

  const deleteHandler = (setData) => async (id) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const backHandler = (setShowForm, setEditing) => () => {
    setShowForm(false);
    if (setEditing) setEditing(null);
  };

  // Specific handlers
  const handleServiceSave = saveHandler(
    setServices,
    setShowServiceForm,
    setEditingService,
    editingService,
  );
  const handleServiceDelete = () => {
    deleteHandler(setServices);
  };
  const handleServiceBack = async () => {
    backHandler(setShowServiceForm, setEditingService)();
    // Refresh services from Redux so next edit has current data
    await getDefaultCompanyServices(companyId);
  };

  const handleTeamSave = saveHandler(
    setTeams,
    setShowTeamForm,
    setEditingTeam,
    editingTeam,
  );
  const handleTeamDelete = deleteHandler(setTeams);
  const handleTeamBack = backHandler(setShowTeamForm, setEditingTeam);

  const handleLocationSave = async (data) => {
    await savePharmacyLocation(data, null);
    await getPharmaciesByCompanyId(companyId);

    trackFirstTimeIntercomEvent({
      eventName: "location_created_first_time",
      user: {
        id: currentUser.emailAddress,
        email: currentUser.emailAddress,
        name: currentUser.fullName,
      },
      properties: {
        source: "location_settings",
      },
    });

    return saveHandler(
      setLocations,
      setShowLocationForm,
      setEditingLocation,
      editingLocation,
    )(data);
  };

  const handleLocationDelete = deleteHandler(setLocations);
  const handleLocationBack = backHandler(
    setShowLocationForm,
    setEditingLocation,
  );

  const handleRoleSave = saveHandler(
    setRoles,
    setShowRoleForm,
    setEditingRole,
    editingRole,
  );
  const handleRoleDelete = deleteHandler(setRoles);
  const handleRoleBack = backHandler(setShowRoleForm, setEditingRole);

  const handleTemplateSave = saveHandler(
    setTemplates,
    setShowTemplateForm,
    setEditingTemplate,
    editingTemplate,
  );
  const handleTemplateDelete = async (id: string) => {
    const templateIds: string[] = [];
    templateIds.push(id);
    await deleteTemplates(templateIds);
    await getEmailsAndSmsTemplates(companyId);
    deleteHandler(setTemplates);
  };
  const handleTemplateBack = backHandler(
    setShowTemplateForm,
    setEditingTemplate,
  );

  const handleConsultationTemplateSave = (templateData) => {
    if (editingConsultationTemplate) {
      setConsultationTemplates((prev) =>
        prev.map((template) =>
          template.id === editingConsultationTemplate.id
            ? { ...templateData, id: editingConsultationTemplate.id }
            : template,
        ),
      );
    } else {
      const newTemplate = {
        ...templateData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0,
      };
      setConsultationTemplates((prev) => [...prev, newTemplate]);
    }
    setShowConsultationForm(false);
    setAiTemplate(null);
    setShowConsultationForm(false);
    setEditingConsultationTemplate(null);
  };

  const handleConsultationTemplateDelete = async (id: string) => {
    setConsultationTemplates((prev) =>
      prev.filter((template) => template.id !== id),
    );

    try {
      await deleteAiTemplateById(id);

      const [_, updatedTemplates] = await Promise.all([
        // getPatientConsultationsCount(companyId),
        getAiTemplateByCompanyId(companyId),
      ]);

      if (updatedTemplates) {
        setConsultationTemplates(updatedTemplates);
      }
    } catch (error) {
      console.error("Failed to delete consultation template:", error);

      const updatedTemplates = await getAiTemplateByCompanyId(companyId);
      if (updatedTemplates) {
        setConsultationTemplates(updatedTemplates);
      }
    }
  };

  const handleConsultationTemplateDuplicate = (template) => {
    const duplicatedTemplate = {
      ...template,
      id: Date.now().toString(),
      name: `${template.name} (Copy)`,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
    };
    setConsultationTemplates((prev) => [...prev, duplicatedTemplate]);
  };

  const handlePreConsultTemplateSave = async (data) => {
    const templateData = data;

    const questionsBySection = (templateData.questions || []).reduce(
      (acc, q) => {
        const section = q?.section || "General Information";
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push({
          label: q?.question,
          required: q?.required ?? true,
          value: q?.value || null,
          userControlId: q?.userControlId || `${q?.id}-${Math.random()}`,
          type: q?.type,
          options: q.options ?? [],
        });
        return acc;
      },
      {} as Record<string, any[]>,
    );

    const templateSections = Object.entries(questionsBySection).map(
      ([sectionTitle, userControls]) => ({
        sectionTitle,
        userControls,
      }),
    );

    const payload = {
      _id: data?.id.startsWith("TEMP-") ? null : data.id,
      templateName: templateData?.name,
      companyID: companyId,
      consultationSummary: templateData.description,
      isActive: templateData.active,
      createdOn: templateData.createdAt,
      templateSections: templateSections,
      // Disclaimer fields
      isIncludeDisclaimer:
        templateData?.isIncludeDisclaimer ??
        templateData?.includeDisclaimer ??
        false,
      disclaimerText: templateData?.disclaimerText || null,
    };

    if (payload?._id) {
      await updatePreConsultTemplates(payload);
    } else {
      await savePreConsultTemplates(payload);

      trackFirstTimeIntercomEvent({
        eventName: "preconsult_template_created_first_time",
        user: {
          id: currentUser.emailAddress,
          email: currentUser.emailAddress,
          name: currentUser.fullName,
        },
        properties: {
          source: "preconsult_settings",
        },
      });
    }
    await saveHandler(
      setPreConsultTemplates,
      setShowPreConsultBuilder,
      setEditingPreConsultTemplate,
      editingPreConsultTemplate,
    )(data);
    await getPreConsultTemplateByCompanyId(companyId);
  };

  const handlePreConsultTemplateDelete = async (id) => {
    await deletePreConsultTemplateById(id);
    await getPreConsultTemplateByCompanyId(companyId);
    deleteHandler(setPreConsultTemplates)(id);
  };

  // Member handlers (simulated API calls)
  const reverseMapAvailability = (availability: any) => {
    const dayMapReverse: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0,
    };

    const result: any[] = [];

    Object.entries(availability || {}).forEach(([dayName, dayValue]: any) => {
      if (dayValue.enabled && dayValue.timeBlocks.length > 0) {
        result.push({
          dayOfWeek: dayMapReverse[dayName],
          slots: dayValue.timeBlocks.map((block: any) => ({
            startTime: block.from,
            endTime: block.to,
          })),
        });
      }
    });

    return result;
  };

  const convertNotificationsToBackend = (notifications) => {
    return [
      {
        type: 1,
        boolValue: notifications.taskAssigned,
        stringValue: null,
        jsonValue: null,
      },
      {
        type: 2,
        boolValue: notifications.taskUpdated,
        stringValue: null,
        jsonValue: null,
      },
      {
        type: 3,
        boolValue: notifications.labResults,
        stringValue: null,
        jsonValue: null,
      },
      {
        type: 4,
        boolValue: null,
        stringValue: null,
        jsonValue: JSON.stringify(notifications.bookingMade),
      },
      {
        type: 5,
        boolValue: null,
        stringValue: null,
        jsonValue: JSON.stringify(notifications.bookingRescheduledOrCancelled),
      },
    ];
  };

  const handleMemberSave = async (memberData) => {
    if (
      !memberData.roleId ||
      !teamRolesAll.some((r) => r._id === memberData.roleId)
    ) {
      showError("Please select a valid role before saving.");
      return;
    }

    const notificationsBackend = convertNotificationsToBackend(
      memberData.notifications,
    );

    const startDate = memberData.startDate || null;
    const endDate = memberData.endDate || null;

    // Map selected pharmacy IDs to full pharmacy objects
    let pharmacyObjects = [];
    const selectedPharmacyIds = (memberData.pharmacies || []).filter(Boolean);

    if (selectedPharmacyIds.length > 0) {
      if (availablePharmacies?.length > 0) {
        pharmacyObjects = availablePharmacies.filter((p) => {
          const pharmacyId = p?.pharmacyID || p?.id || p?._id;
          return (
            pharmacyId && selectedPharmacyIds.includes(pharmacyId.toString())
          );
        });
      }
      if (pharmacyObjects.length === 0 && companyId) {
        try {
          const loadedPharmacies = await getPharmaciesByCompanyId(companyId);
          pharmacyObjects =
            loadedPharmacies?.filter((p) => {
              const pharmacyId = p?.pharmacyID || p?.id || p?._id;
              return (
                pharmacyId &&
                selectedPharmacyIds.includes(pharmacyId.toString())
              );
            }) || [];
        } catch (error) {
          console.error("Error loading pharmacies for mapping:", error);
        }
      }
    }

    const formattedPharmacyObjects = pharmacyObjects.map((p) => ({
      _id: p._id || p.id,
      pharmacyID: p.pharmacyID || p.id,
      pharmacyName: p.pharmacyName,
    }));
    const addMemberPayload = {
      userId: memberData.id ?? "",
      companyID: memberData.companyID ?? "",
      emailAddress: memberData.email ?? "",
      firstName: memberData.forename ?? "",
      gender: memberData.gender ?? null,
      lastName: memberData.surname ?? "",
      userImage: memberData.userImage ?? "",
      isNew: memberData.isNew ?? false,
      title: memberData.title ? parseInt(memberData.title, 10) || 0 : 0,
      isClinician: memberData.isClinician ?? false,
      qualification: memberData.qualifications
        ? memberData.qualifications.join(", ")
        : "",
      registrations: memberData.registrations
        ? memberData.registrations.join(", ")
        : "",
      accountType: memberData.accountType ?? null,
      isVerified: memberData.isVerified ?? false,
      isActive: memberData.active ?? false,
      newCompanyID: memberData.newCompanyID ?? null,
      lastLogin: memberData.lastLogin ?? null,
      userStatus: memberData.userStatus ?? null,
      phoneNumber: memberData.phoneNumber ?? "",
      role: teamRolesAll.find((r) => r._id === memberData.roleId) || null,
      // update-user-v2 takes ShortServiceModel, not the full ServiceModel.
      services: defaultPharmacyServices
        .filter((s) => memberData.servicesProvided?.includes(s._id))
        .map((s) => ({
          _id: s._id,
          serviceType: s.serviceType,
          serviceName: s.serviceName,
          serviceCharges: s.serviceCharges,
          serviceDuration: s.serviceDuration,
          serviceColor: s.serviceColor,
          serviceCode: s.serviceCode,
          isOnlineBooking: s.isOnlineBooking ?? false,
          availability: s.availability,
          isActive: s.isActive,
          allowGroupBookings: s.allowGroupBookings,
          maxGroupSize: s.maxGroupSize,
          serviceChargesType: s.serviceChargesType,
        })),
      pharmacies: pharmacyObjects, // Will be empty array if no pharmacies selected or available
      notifications: notificationsBackend || [],
      userWeekAvailability: reverseMapAvailability(memberData.availability),
      startDate:
        startDate === null ? startDate : new Date(startDate).toISOString(),
      endDate: endDate === null ? endDate : new Date(endDate).toISOString(),
    };

    await updateMemberV2(addMemberPayload);
    await getmembers(companyId);

    trackFirstTimeIntercomEvent({
      eventName: "member_added_first_time",
      user: {
        id: currentUser.emailAddress,
        email: currentUser.emailAddress,
        name: currentUser.fullName,
      },
      properties: {
        source: "members_settings",
      },
    });

    if (globalTabText === "Documents") {
      await getCompanyDocumentsByCompanyId(companyId);
    }

    setShowMemberForm(false);
    setEditingMember(null);
  };

  const handleMemberDelete = async (email) => {
    await deleteUserById(email);
    await getmembers(companyId);
    setMembers((prev) => prev.filter((member) => member.email !== email));
  };

  /**
   * Handle inviting a new member with date range support
   * @param {InviteData} inviteData - The invitation data including email, role, teams, and optional dates
   */
  const handleInviteMember = (inviteData) => {
    if (!inviteData.roleId) {
      showError("Please select a role before sending the invitation.");
      return;
    }

    const role = { _id: inviteData.roleId };
    const myArray = inviteData.email.split(",");
    const teamIds = inviteData.selectedTeams;

    // Get dates if they exist (when hasDateRange is true in the form)
    const startDate = inviteData.startDate ?? null;
    const endDate = inviteData.endDate ?? null;

    // Create base members using the first team (as a template)
    let baseMembers = new setinviteTeam(myArray, teamIds[0], companyId, role)
      .obj;

    let teamMembers = [];

    teamIds.forEach((teamId) => {
      baseMembers.forEach((member) => {
        // Create a member object with dates included
        const teamMember = {
          ...member,
          teamMemberID: teamId,
        };

        // Add start date if provided
        if (startDate) {
          teamMember.startDate = startDate;
        }

        // Add end date if provided
        if (endDate) {
          teamMember.endDate = endDate;
        }

        teamMembers.push(teamMember);
      });
    });

    // Prepare final payload
    const finalPayload = {
      teamIds,
      teamMembers,
    };

    // Add dates to the root payload as well
    if (startDate) {
      finalPayload.startDate = startDate;
    }

    if (endDate) {
      finalPayload.endDate = endDate;
    }

    // Optional: Log for debugging

    inviteMemberMultipleTeams(finalPayload)
      .then((data) => {
        if (data === null) {
          showError("There is some error sending invite to member!");
        } else {
          setShowInviteMember(false);
          // Optionally refresh the pending invites list
          getPendingInvites(companyId);
        }
      })
      .catch((error) => {
        showError(error.message || "Error sending invitation");
      });
  };

  const handleResendInvite = useCallback(
    async (userEmail: string, memberId: string) => {
      try {
        await reinviteEmailToUser(memberId);
        // Optimistically update lastSentAt for the matching invite
        setPendingInvites((prev) =>
          prev.map((invite) =>
            invite.userEmail === userEmail
              ? { ...invite, invitationDate: new Date().toISOString() }
              : invite,
          ),
        );
      } catch (error) {
        console.error("Error resending invite:", error);
      }
    },
    [],
  );

  const handleDeleteInvite = useCallback(
    async (inviteId: string) => {
      confirmDelete(async () => {
        try {
          await deleteInvite(inviteId);
          await getPendingInvites(companyId);
        } catch (error) {
          console.error("Error deleting invite:", error);
        }
      }, "Are you sure you want to delete this invite?");
    },
    [companyId],
  );

  // Room handlers
  interface Service {
    id: string;
    name: string;
    price: string;
    duration: number;
    color: string;
    active: boolean;
    serviceData: object;
    serviceType: number;
    serviceCode: number;
  }

  const LoadServices = async (id) => {
    const response = await getServicesByPharmacyId(id);
    const formattedServices: Service[] =
      response?.map((s: any) => ({
        id: s._id,
        name: s.serviceName,
        price: String(s.serviceCharges),
        duration: s.serviceDuration,
        color: s.serviceColor || "",
        active: !!s.isDefault,
        serviceData: s,
        serviceType: s.serviceType,
        serviceCode: s.serviceCode,
      })) || [];
    setServices(formattedServices);
    return formattedServices;
  };

  const handleAddRoom = async (location) => {
    await LoadServices(location?.id);
    setSelectedLocation(location);
    setEditingRoom(null);
    setShowRoomForm(true);
  };

  const addRoom = async (data) => {
    await LoadServices(data.id);
  };

  const handleEditRoom = async (location, room) => {
    await LoadServices(location?.id);
    setSelectedLocation(location);
    setEditingRoom(room);
    setShowRoomForm(true);
  };

  const handleRoomSave = async (roomData) => {
    var payload = {
      PharmacyID: selectedLocation.id,
      PharmacyRoom: {
        _id:
          roomData.id && ObjectId.isValid(roomData.id)
            ? roomData.id
            : new ObjectId().toHexString(),
        roomName: roomData.name,
        services: (roomData.services || []).map((s) => {
          const fullService = services.find((srv) => srv.name === s);
          return fullService ? fullService.serviceData : { serviceName: s };
        }),
        availability: reverseAvailability(roomData.availability),
        active: roomData.active,
      },
    };
    if (roomData.id) {
      await editPharmacyRoom(payload);
    } else {
      await addPharmacyRoom(payload);
      await getPharmaciesByCompanyId(companyId);
    }
    setLocations((prev) =>
      prev.map((location) => {
        if (location.id !== selectedLocation.id) return location;
        if (editingRoom) {
          return {
            ...location,
            rooms: location.rooms.map((room) =>
              room.id === editingRoom.id
                ? { ...roomData, id: editingRoom.id }
                : room,
            ),
          };
        } else {
          const newRoom = { ...roomData, id: `r${Date.now()}` };
          return { ...location, rooms: [...(location.rooms || []), newRoom] };
        }
      }),
    );
    setShowRoomForm(false);
    setEditingRoom(null);
    setSelectedLocation(null);
  };

  const handleRoomDelete = async (locationId: string, roomId: string) => {
    let updatedLocation;

    setLocations((prev) => {
      const updated = prev.map((location) => {
        if (location.id !== locationId) return location;

        return {
          ...location,
          rooms: location.rooms.filter((room) => room.id !== roomId),
        };
      });
      updatedLocation = updated.find((loc) => loc.id === locationId);
      return updated;
    });

    if (updatedLocation) {
      try {
        const servicesData = await LoadServices(locationId);
        await savePharmacyLocation(updatedLocation, servicesData);
      } catch (err) {
        console.error("Failed to update pharmacy location:", err);
      }
    }
  };

  // Service utility handlers
  const handleShowQRCode = (service) => {
    setSelectedService(service);
    setShowQRCode(true);
  };

  // App handlers
  const handleAppInstall = (appId, selectedLocationIds, apiKeys) => {
    setInstalledApps((prev) => {
      const newMap = new Map(prev);
      const existingInstall = newMap.get(appId) || {
        locations: [],
        apiKeys: {},
      };
      const allLocations = [
        ...new Set([...existingInstall.locations, ...selectedLocationIds]),
      ];
      const mergedApiKeys = { ...existingInstall.apiKeys, ...apiKeys };

      newMap.set(appId, { locations: allLocations, apiKeys: mergedApiKeys });
      return newMap;
    });
  };

  const handleAppUninstall = (appId, locationId = null) => {
    setInstalledApps((prev) => {
      const newMap = new Map(prev);
      if (locationId) {
        const existingInstall = newMap.get(appId);
        if (existingInstall) {
          const newLocations = existingInstall.locations.filter(
            (id) => id !== locationId,
          );
          const newApiKeys = { ...existingInstall.apiKeys };
          delete newApiKeys[locationId];

          if (newLocations.length === 0) {
            newMap.delete(appId);
          } else {
            newMap.set(appId, { locations: newLocations, apiKeys: newApiKeys });
          }
        }
      } else {
        newMap.delete(appId);
      }
      return newMap;
    });
  };

  const handleAppApiKeysUpdate = (appId, locationId, apiKeys) => {
    setInstalledApps((prev) => {
      const newMap = new Map(prev);
      const existingInstall = newMap.get(appId);
      if (existingInstall) {
        newMap.set(appId, {
          ...existingInstall,
          apiKeys: { ...existingInstall.apiKeys, [locationId]: apiKeys },
        });
      }
      return newMap;
    });
  };

  const handlePreConsultTemplateProceedToBuilder = useCallback(
    (templateData) => {
      const newTemplate = {
        ...templateData,
        id: "TEMP-" + Date.now(),
        createdAt: new Date().toISOString(),
        responseCount: 0,
      };

      // Don't add to preConsultTemplates here; it only appears in the list
      // once saved (handlePreConsultTemplateSave re-fetches from the server).
      setEditingPreConsultTemplate(newTemplate);

      setShowPreConsultForm(false);
      setShowPreConsultBuilder(true);
    },
    [],
  );

  const confirmDelete = (action: () => void, message?: string) => {
    setDeleteAction(() => action);
    if (message) setDeleteMessage(message);
    setDeleteModalOpen(true);
  };

  const renderTabContent = () => {
    if (
      showServiceForm &&
      activeTab === "services" &&
      ifKanbanSearchBoxEnabled
    ) {
      return (
        <ServiceFormPanel
          service={editingService}
          templates={templates}
          onSave={handleServiceSave}
          onBack={handleServiceBack}
        />
      );
    }

    if (showTeamForm && activeTab === "teams") {
      return (
        <TeamFormPanel
          team={editingTeam}
          onSave={handleTeamSave}
          onBack={handleTeamBack}
        />
      );
    }

    if (showMemberForm && activeTab === "members") {
      return (
        <MemberFormPanel
          member={editingMember}
          onSave={handleMemberSave}
          onBack={() => {
            setShowMemberForm(false);
            setEditingMember(null);
          }}
        />
      );
    }
    if (showLocationForm && activeTab === "locations") {
      return (
        <LocationFormPanel
          location={editingLocation}
          services={services}
          isDarkMode={isDarkMode}
          onSave={handleLocationSave}
          onBack={handleLocationBack}
          onAddRoom={addRoom}
          isUpdateLocationLoading={
            isUpdatePharmacyLocationLoading || isPharmacyServiceLoading
          }
          confirmDelete={confirmDelete}
        />
      );
    }

    if (showRoleForm && activeTab === "roles") {
      return (
        <RoleFormPanel
          role={editingRole}
          onSave={handleRoleSave}
          onBack={handleRoleBack}
        />
      );
    }

    if (showTemplateForm && activeTab === "templates") {
      return (
        <TemplateFormPanel
          template={editingTemplate}
          defaultType={defaultTemplateType}
          onSave={handleTemplateSave}
          onBack={handleTemplateBack}
        />
      );
    }

    if (showConsultationForm && activeTab === "consultation") {
      return (
        <ConsultationFormPanel
          template={editingConsultationTemplate}
          onSave={handleConsultationTemplateSave}
          onBack={() => {
            setShowConsultationForm(false);
            setAiTemplate(null);
            setEditingConsultationTemplate(null);
          }}
          onReview={handleConsultationTemplateReview}
          confirmDelete={confirmDelete}
        />
      );
    }

    if (showPreConsultForm && activeTab === "preconsult") {
      return (
        <PreConsultTemplateFormPanel
          isDarkMode={isDarkMode}
          onSave={handlePreConsultTemplateSave}
          onProceedToBuilder={handlePreConsultTemplateProceedToBuilder}
          onBack={() => setShowPreConsultForm(false)}
        />
      );
    }

    if (showPreConsultBuilder && activeTab === "preconsult") {
      return (
        <PreConsultTemplateBuilderPanel
          template={editingPreConsultTemplate}
          onSave={handlePreConsultTemplateSave}
          onBack={() => {
            setShowPreConsultBuilder(false);
            setEditingPreConsultTemplate(null);
          }}
          confirmDelete={confirmDelete}
        />
      );
    }

    if (showInviteMember && activeTab === "members") {
      return (
        <InviteMemberPanel
          teams={teams}
          onInvite={handleInviteMember}
          onBack={() => setShowInviteMember(false)}
        />
      );
    }

    if (showQRCode && activeTab === "services") {
      return (
        <ServiceQRCodePanel
          service={selectedService}
          onBack={() => {
            setShowQRCode(false);
            setSelectedService(null);
          }}
        />
      );
    }

    if (showRoomForm && activeTab === "locations") {
      return (
        <RoomFormInLocation
          location={selectedLocation}
          isDarkMode={isDarkMode}
          room={editingRoom}
          services={services}
          onSave={handleRoomSave}
          onCancel={() => {
            setShowRoomForm(false);
            setEditingRoom(null);
            setSelectedLocation(null);
          }}
        />
      );
    }

    if (showAppDetails && activeTab === "marketplace") {
      const selectedAppName = selectedApp?.name?.trim().toLowerCase();

      if (selectedAppName === "consultation bundles") {
        return (
          <ConsultationBundlesAppDetails
            app={selectedApp}
            installedAppData={installedApps.get(selectedApp?.id)}
            isDarkMode={isDarkMode}
            onInstall={handleAppInstall}
            onBack={() => {
              setShowAppDetails(false);
              setSelectedApp(null);
            }}
          />
        );
      }

      if (selectedAppName === "allied health training pgd") {
        return (
          <AlliedHealthTrainingPgdAppDetails
            isDarkMode={isDarkMode}
            onBack={() => {
              setShowAppDetails(false);
              setSelectedApp(null);
            }}
          />
        );
      }

      return (
        <AppDetailsPanel
          app={selectedApp}
          installedAppData={installedApps.get(selectedApp?.id)}
          locations={locations}
          onInstall={handleAppInstall}
          onUninstall={handleAppUninstall}
          onApiKeysUpdate={handleAppApiKeysUpdate}
          onBack={() => {
            setShowAppDetails(false);
            setSelectedApp(null);
          }}
        />
      );
    }

    // Main tab views
    switch (activeTab) {
      case "general":
        return <GeneralSettingsTab />;

      case "locations":
        return (
          <LocationsSettingsTab
            locations={locations}
            services={services}
            isDarkMode={isDarkMode}
            onAddLocation={() => setShowLocationForm(true)}
            onEditLocation={async (location) => {
              await LoadServices(location.id);
              createHandler(
                null,
                setShowLocationForm,
                setEditingLocation,
              )(location);
            }}
            onDeleteLocation={(location) =>
              confirmDelete(() => handleLocationDelete(location), "location")
            }
            onAddRoom={handleAddRoom}
            onEditRoom={handleEditRoom}
            onDeleteRoom={(locationId, roomId) => {
              const location = locations.find((l) => l.id === locationId);
              const room = location?.rooms?.find((r) => r.id === roomId);
              if (room?.services?.length > 0) {
                showError(
                  "This room cannot be deleted because it has services linked to it. Please remove the services from the room first.",
                );
                return;
              }
              confirmDelete(
                () => handleRoomDelete(locationId, roomId),
                deleteConfirmationMessage + "room",
              );
            }}
            onNavigateToBilling={async () => {
              setActiveTab("billing");
              await switchTabWithData("billing");
            }}
          />
        );

      case "members":
        return (
          <MembersSettingsTab
            onInviteMember={() => setShowInviteMember(true)}
            onEditMember={createHandler(
              null,
              setShowMemberForm,
              setEditingMember,
            )}
            onDeleteMember={(member) =>
              confirmDelete(
                () => handleMemberDelete(member),
                deleteConfirmationMessage + "member",
              )
            }
            onResendInvite={handleResendInvite}
            onDeleteInvite={handleDeleteInvite}
          />
        );

      case "teams":
        return (
          <TeamsSettingsTab
            teams={teams}
            onAddTeam={() => setShowTeamForm(true)}
            onEditTeam={createHandler(null, setShowTeamForm, setEditingTeam)}
            onDeleteTeam={handleTeamDelete}
            confirmDelete={confirmDelete}
          />
        );

      case "roles":
        return (
          <RolesSettingsTab
            roles={roles}
            onAddRole={() => setShowRoleForm(true)}
            onEditRole={createHandler(null, setShowRoleForm, setEditingRole)}
            onDeleteRole={(role) =>
              confirmDelete(
                () => handleRoleDelete(role),
                deleteConfirmationMessage + "role",
              )
            }
          />
        );

      case "services":
        return (
          <ServicesSettingsTab
            onAddService={() => setShowServiceForm(true)}
            onEditService={createHandler(
              null,
              setShowServiceForm,
              setEditingService,
            )}
            onShowQRCode={handleShowQRCode}
            confirmDelete={confirmDelete}
          />
        );

      case "templates":
        return (
          <TemplatesSettingsTab
            templates={emailAndSmsTemplates}
            onAddTemplate={(defaultType) => {
              setDefaultTemplateType(defaultType);
              setShowTemplateForm(true);
            }}
            onEditTemplate={createHandler(
              null,
              setShowTemplateForm,
              setEditingTemplate,
            )}
            onDeleteTemplate={(template) =>
              confirmDelete(
                () => handleTemplateDelete(template),
                deleteConfirmationMessage + "template",
              )
            }
          />
        );

      case "preconsult":
        return (
          <PreConsultSettingsTab
            templates={preConsultTemplates}
            onAddTemplate={() => setShowPreConsultForm(true)}
            onEditTemplate={(template) => {
              setEditingPreConsultTemplate(template);
              setShowPreConsultBuilder(true);
            }}
            onDeleteTemplate={(template) =>
              confirmDelete(
                () => handlePreConsultTemplateDelete(template),
                deleteConfirmationMessage + "template",
              )
            }
          />
        );

      case "marketplace":
        return (
          <MarketplaceSettingsTab
            onViewApp={(app) => {
              setSelectedApp(app);
              setShowAppDetails(true);
            }}
          />
        );

      case "consultation":
        return (
          <ConsultationSettingsTab
            onAddTemplate={() => setShowConsultationForm(true)}
            onEditTemplate={createHandler(
              null,
              setShowConsultationForm,
              setEditingConsultationTemplate,
            )}
            onDeleteTemplate={(template) =>
              confirmDelete(
                () => handleConsultationTemplateDelete(template),
                deleteConfirmationMessage + "consultation",
              )
            }
            onDuplicateTemplate={handleConsultationTemplateDuplicate}
          />
        );

      case "billing":
        return <BillingSettingsTab />;

      default:
        return <GeneralSettingsTab />;
    }
  };

  const handleConsultationTemplateReview = useCallback((reviewedTemplate) => {
    setConsultationTemplates((prev) =>
      prev.map((template) =>
        template.id === reviewedTemplate.id ? reviewedTemplate : template,
      ),
    );
  }, []);
  const hasTemplatePermission =
    checkUserPermissions(accessTitanverseTemplateView) ||
    checkUserPermissions(accessTitanverseTemplateUpsert);

  const hasUserPermission =
    checkUserPermissions(accessTitanversUserView) ||
    checkUserPermissions(accessTitanversUserUpsert);

  const hasTeamPermission =
    checkUserPermissions(accessTitanverseTeamUpsert) ||
    checkUserPermissions(accessTitanverseTeamView);
  // const hasRolePermission =
  //   currentUser?.role?.roleName?.toLowerCase() === "admin" ||
  //   currentUser?.role?.roleName?.toLowerCase() === "superadmin" ||
  //   checkUserPermissions(accessTitanverseFullAccess);
  const hasManageSettingsPermission = checkUserPermissions(
    accessTitanverseManageSettings,
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`max-w-6xl h-[90vh] overflow-hidden p-0 flex flex-col transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader
          className={`p-6 pb-4 border-b flex-shrink-0 transition-colors duration-300 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <DialogTitle
              className={`text-xl font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Settings
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Sidebar - Tabs */}
          <div
            className={`w-64 border-r p-4 flex-shrink-0 transition-colors duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <nav className="space-y-2">
              {tabs.map((tab) => {
                // Billing tab visibility: ONLY show when subscription status is 2
                const isSubscriptionActive =
                  generalData?.companySubscriptionStatus === 2;

                // Other features condition
                const isBillingConditionMet =
                  ifBillingEnabled ||
                  generalData?.companySubscriptionStatus === 2;

                if (
                  (["services", "marketplace"].includes(tab.id) &&
                    !ifKanbanSearchBoxEnabled) ||
                  (tab.id === "billing" && !isSubscriptionActive)
                )
                  return false;

                if (
                  tab.id === "templates" &&
                  !(
                    (ifAIConsultationEnabled || isBillingConditionMet) &&
                    hasTemplatePermission
                  )
                ) {
                  return null;
                }
                if (tab.id === "members" && !hasUserPermission) {
                  return null;
                }
                if (tab.id === "teams" && !hasTeamPermission) {
                  return null;
                }

                if (
                  ["marketplace", "locations", "roles", "services"].includes(
                    tab.id,
                  ) &&
                  !(ifAIConsultationEnabled || isBillingConditionMet)
                ) {
                  return null;
                }

                if (
                  ["locations", "services", "marketplace"].includes(tab.id) &&
                  !hasManageSettingsPermission
                ) {
                  return null;
                }

                const IconComponent = tab.icon;
                const hasChildren = tab.children && tab.children.length > 0;
                const isExpanded = expandedSections.has(tab.id);
                const isActive = activeTab === tab.id;
                const hasActiveChild = tab.children?.some(
                  (child) => child.id === activeTab,
                );

                return (
                  <div key={tab.id}>
                    <button
                      disabled={isConsultationSummaryLoading}
                      onClick={async () => {
                        if (hasChildren) {
                          const newExpanded = new Set(expandedSections);
                          if (isExpanded) {
                            newExpanded.delete(tab.id);
                          } else {
                            newExpanded.add(tab.id);
                          }
                          setExpandedSections(newExpanded);
                        } else {
                          if (activeTab === tab.id) return;
                          setActiveTab(tab.id);
                          await switchTabWithData(tab.id);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors duration-300 ${
                        isActive || (hasChildren && hasActiveChild)
                          ? isDarkMode
                            ? "bg-blue-600 text-white"
                            : "bg-slate-900 text-white"
                          : isDarkMode
                          ? "text-gray-300 hover:bg-gray-700"
                          : "text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span className="flex-1">{tab.label}</span>
                      {hasChildren && (
                        <div className="transition-transform duration-200">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      )}
                    </button>

                    {/* Children/Subsections */}
                    {hasChildren && isExpanded && (
                      <div className="mt-1 ml-6 space-y-1">
                        {tab.children!.map((child) => {
                          const ChildIconComponent = child.icon;
                          const isChildActive = activeTab === child.id;
                          return (
                            <button
                              disabled={isConsultationSummaryLoading}
                              key={child.id}
                              onClick={async () => {
                                if (activeTab === child.id) return;
                                setActiveTab(child.id);
                                await switchTabWithData(child.id);
                              }}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors duration-300 ${
                                isChildActive
                                  ? isDarkMode
                                    ? "bg-blue-500 text-white"
                                    : "bg-slate-800 text-white"
                                  : isDarkMode
                                  ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                              }`}
                            >
                              <ChildIconComponent className="w-4 h-4" />
                              {child.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Right Content Area */}
          <div
            className={`flex-1 overflow-y-auto min-h-0 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-900" : "bg-white"
            }`}
          >
            {renderTabContent()}
          </div>
        </div>
        <PermissionRequiredModal
          open={showPermissionModal}
          onClose={() => setShowPermissionModal(false)}
          requiredPermission={showPermissionMessage}
        />
      </DialogContent>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={deleteAction}
        title="Delete Confirmation"
        message={deleteMessage}
        isDarkMode={isDarkMode}
      />
    </Dialog>
  );
}
